import React, { useRef, useState } from 'react';
import { getPercentageInfo, mathFormat } from '@/pages/opinion/utils';
import { formatter } from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import EChartCommon from '@/pages/opinion/components/EChartCommon';
import { chartAllColors } from '@/pages/opinion/constants';

export default function ChartCommonPie({
  chartData,
  loading,
  title = '',
  mentionsTotal,
  height = 300,
  handleOnClick,
  pieLabelAlignTo,
  chartOptions = {},
  transFunc = (name: string) => name,
  defaultSelected,
}: any) {
  const { formatMessage } = useIntl();
  const pieSelected = useRef(defaultSelected);
  const eChartsRef = React.useRef(null as any);

  const legendData: Array<any> = [];
  const newCharData = chartData?.map((v: { name: any }, index: number) => {
    const color = chartAllColors[index];
    const name = transFunc(v?.name);
    legendData.push({
      name: name,
      icon: 'circle',
      itemStyle: {
        color: color,
      },
    });

    return {
      ...v,
      name: name,
      selected: pieSelected.current === v?.name,
      originName: v.name,
      itemStyle: {
        color: color,
      },
    };
  });

  const options = {
    legend: {
      data: legendData,
    },
    tooltip: {
      borderWidth: 0,
      borderRadius: 6,
      padding: 0,
      formatter: (params: any) => {
        return formatter({
          title: params.name,
          tables: [
            {
              showHeader: false,
              records: [
                {
                  name: formatMessage({ id: 'Mentions' }),
                },
              ],
              columns: [
                {
                  key: 'name',
                },
                {
                  weight: 'bold',
                  align: 'right',
                  key: 'score',
                  render: () => {
                    let percent = mentionsTotal
                      ? getPercentageInfo(params?.value, mentionsTotal, 2)
                      : `${params.percent.toFixed(2)}%`;
                    return `${mathFormat(params?.value)}(${percent})`;
                  },
                },
              ],
            },
          ],
        });
      },
    },
    title: [
      {
        text: '{name|' + mathFormat(mentionsTotal) + '}',
        top: '40%',
        left: 'center',
        textStyle: {
          rich: {
            name: {
              fontSize: 24,
              fontFamily: 'num-m',
              color: '#000000',
              padding: [10, 0],
            },
          },
        },
      },
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '34%',
        style: {
          fill: '#444444',
          fontSize: 12,
          text: title || '',
        },
      },
    ],
    series: [
      {
        labelLayout: (params: any) => {
          const isLeft =
            params.labelRect.x <
            eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
          const points = params.labelLinePoints;
          // Update the end point.
          points[2][0] = isLeft
            ? params.labelRect.x
            : params.labelRect.x + params.labelRect.width;
          return {
            labelLinePoints: points,
          };
        },
        label: {
          color: 'auto',
          alignTo: pieLabelAlignTo,
          formatter: (params: any) => {
            let percent = mentionsTotal
              ? getPercentageInfo(params?.value, mentionsTotal, 2)
              : `${params.percent.toFixed(2)}%`;
            return (
              '{value| ' +
              params.name +
              ' }：{a|' +
              mathFormat(params.value) +
              '(' +
              percent +
              ')}\n{hr|}\n{a|}'
            );
          },
          edgeDistance: 10,
          rich: {
            a: {
              // color: '#999',
              lineHeight: 20, //设置最后一行空数据高度，为了能让延长线与hr线对接起来
              align: 'center',
            },
            hr: {
              //设置hr是为了让中间线能够自适应长度
              borderColor: 'auto', //hr的颜色为auto时候会主动显示颜色的
              width: '105%',
              borderWidth: 0.5,
              height: 0.5,
            },
            per: {
              //用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
              padding: [4, 0],
            },
          },
        },
        data: newCharData || [],
      },
    ],
  };

  return (
    <EChartCommon
      empty={!chartData || chartData.length === 0}
      optionsProps={{
        ...chartOptions,
        type: 'pie',
        pieLabelAlignTo: pieLabelAlignTo || 'edge',
      }}
      ref={eChartsRef}
      options={options}
      loading={loading}
      height={height}
      eventsDict={{
        click: (params: any) => {
          let value = params?.data?.originName;
          // 如果进入选中模式
          if (chartOptions.selectedMode) {
            const { originName } = params.data || {};
            value = pieSelected.current === originName ? '' : originName;
            pieSelected.current = value;
          }
          handleOnClick?.(
            {
              ...params,
              data: {
                ...params.data,
                originName: value,
              },
            },
            eChartsRef,
          );
        },
      }}
    />
  );
}
